import { usePharmacyStore } from '../../stores/pharmacyStore';
import { useInboxRange } from '../../stores/communicationFilterStore';
import { useInboxCounts } from './useConversationInbox';

/**
 * Unread-conversation count for the app-shell nav badge (GP-1400).
 *
 * The counts endpoint was only ever called by the Communication Center page on
 * mount, so a small team had to open /communications to discover replies were
 * waiting. This lifts the same query into the shell:
 *
 *  - SAME query key as the page (`useInboxCounts` + the shared range store), so
 *    the two share one cache entry and one network call. Reading a conversation
 *    invalidates that key and the badge drops with the page's chip; if the
 *    socket delivers a `conversation-upsert`, the page's sync hook invalidates
 *    the same key and the badge follows.
 *  - No department filter — the page always mounts on "All Departments", so the
 *    badge count is exactly the number behind the page's Unread chip.
 *  - `undefined` (not 0) until the count is actually known, so the nav never
 *    paints a placeholder while the first fetch is in flight.
 *
 * Background polling comes from useInboxCounts (60s, `refetchIntervalInBackground`).
 *
 * @param enabled pass false when the caller doesn't render the badge (e.g. the
 *   nav item is hidden for this role) so we don't poll for a count nobody sees.
 */
export function useUnreadConversationBadge(enabled = true): number | undefined {
  const activePharmacyId = usePharmacyStore((state) => state.activePharmacy?.id);
  const range = useInboxRange();

  const { data, isSuccess } = useInboxCounts(
    enabled ? activePharmacyId : undefined,
    undefined,
    range,
  );

  return isSuccess ? data?.unread : undefined;
}
